import React from "react";
import classNames from "classnames";
import { Search_Icon } from "../../../assets/images";
import {
  ArrowDownwardTwoTone,
  ArrowUpwardTwoTone,
  DeleteOutline,
  MyLocation,
  PeopleSharp,
  HouseSharp,
  Edit,
} from "@mui/icons-material";
import moment from "moment/moment";
import { useNavigate, useParams } from "react-router-dom";
import {
  useFetchFacilityByIdMutation,
  useFetchFacilityPatientsMutation,
  useFetchFacilityProvidersMutation,
  useFetchFacilityStaffMutation,
} from "../../../features/facility/facilityApiSlice";
import ReusableModal from "../../../components/Modals/ReusableModal";
import CloseSvg from "../../../assets/svgs/CloseSvg";
import {
  useCreateStaffUserMutation,
  useRemoveUserMutation,
} from "../../../features/user/userApiSlice";
import { useSelector } from "react-redux";
import { selectUser } from "../../../features/auth/authSlice";
import Search from "../../../components/Search/SearchNew";
import PatientsTable from "./components/PatientsTable";
import CommunityZoneEditModal from "./components/CommunityZoneEditModal";
import { Tooltip } from "@mui/material";
import Loading from "../../../components/Loading";

const CommunityInfo = () => {
  const [patients, setPatients] = React.useState([]);
  const [providers, setProviders] = React.useState([]);
  const [staff, setStaff] = React.useState([]);
  const [community, setCommunity] = React.useState(null);
  const [zones, setZones] = React.useState([]);
  const [zoneEditModalOpen, setZoneEditModalOpen] = React.useState(false);

  const [staffFirstName, setStaffFirstName] = React.useState("");
  const [staffLastName, setStaffLastName] = React.useState("");
  const [staffEmail, setStaffEmail] = React.useState("");
  const [staffPhone, setStaffPhone] = React.useState("");
  const [staffRole, setStaffRole] = React.useState(0);
  const [error, setError] = React.useState("");

  const [addStaffOpen, setAddStaffOpen] = React.useState(false);

  const [searchFilter, setSearchFilter] = React.useState("");

  const [tab, setTab] = React.useState("patients");

  const handleTabChange = (newTab) => {
    if (newTab !== tab) {
      setTab(newTab);
    }
  };

  const { id } = useParams();

  const navigate = useNavigate();

  const handlePatientClick = (patient) => {
    navigate(`/data-management/patients/${patient.id}`);
  };

  const handleProviderClick = (provider) => {
    // navigate(`/data-management/providers/${provider.id}`);

    let url = `/data-management/providers/${provider.id}`;
    if (provider.provider_market_id) {
      url += `?market=${provider.provider_market_id}`;
    }
    navigate(url);

  };

  const [getPatients] = useFetchFacilityPatientsMutation();
  const [getProviders] = useFetchFacilityProvidersMutation();
  const [getStaff] = useFetchFacilityStaffMutation();
  const [createStaffUser, { isLoading: isCreatingStaffUser }] =
    useCreateStaffUserMutation();
  const [removeUser, { isLoading: isRemovingUser }] = useRemoveUserMutation();

  const isLoading = isRemovingUser;

  const user = useSelector(selectUser);

  const saveCommunityUser = async () => {
    try {
      const { data } = await createStaffUser({
        userData: {
          type: 2,
          email: staffEmail,
          phone: staffPhone || null,
        },
        staffData: {
          organisation_id: user.organisation.id,
          community_id: community?.community_id ?? id,
          firstname: staffFirstName,
          lastname: staffLastName,
          role: staffRole,
        },
      }).unwrap();

      const staffList = [data, ...staff];
      setStaff(staffList);
      setAddStaffOpen(false);
      clearInput();
    } catch (error) {
      setError(error.data.message);
    }
  };

  const removeCommunityUser = async (userId) => {
    await removeUser({
      userId,
    }).unwrap();

    const staffList = staff.filter((staffMember) => staffMember.id !== userId);
    setStaff(staffList);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const [getFacilityById] = useFetchFacilityByIdMutation();

  const fetchPatients = async () => {
    try {
      const { data } = await getPatients(id).unwrap();

      setPatients(data);
    } catch (error) {
      console.error(error.data.message);
    }
  };

  const fetchProviders = async () => {
    try {
      const { data } = await getProviders(id).unwrap();

      setProviders(data);
    } catch (error) {
      console.error(error.data.message);
    }
  };

  const fetchStaff = async () => {
    try {
      const { data } = await getStaff(id).unwrap();

      setStaff(data);
    } catch (error) {
      console.error(error.data.message);
    }
  };

  const fetchCommunity = async () => {
    try {
      const { data } = await getFacilityById(id).unwrap();

      setCommunity(data ?? null);
    } catch (error) {
      console.error(error.data?.message ?? error.message);
    }
  };

  React.useEffect(() => {
    if (id) {
      fetchCommunity();
      fetchPatients();
      fetchStaff();
      fetchProviders();
    }
  }, [id]);

  React.useEffect(() => {
    const patientZones = Array.from(
      new Set(
        patients
          .map((patient) => patient.zones)
          .flat()
          .map((zone) => JSON.stringify(zone))
      )
    ).map((zone) => JSON.parse(zone));

    const providerZones = Array.from(
      new Set(
        providers
          .map((provider) => provider.zones)
          .flat()
          .map((zone) => JSON.stringify(zone))
      )
    ).map((zone) => JSON.parse(zone));

    const uniqueZones = Array.from(
      new Set(
        patientZones
          .concat(providerZones)
          .flat()
          .map((zone) => JSON.stringify(zone))
      )
    ).map((zone) => JSON.parse(zone));

    setZones(uniqueZones);
  }, [patients, providers]);

  const updateCommunityZones = async (_communityId, zones) => {
    setZones(zones);
  };

  const Avatar = ({
    name,
    image,
    size = "w-10 h-10",
    textSize = "text-base",
    fontWeight = "font-normal",
  }) => {
    return (
      <>
        {image !== null ? (
          <div className="flex gap-2 items-center">
            <img
              src={image}
              alt={name}
              className={classNames(
                "object-cover w-10 h-10 rounded-full",
                size
              )}
            />
          </div>
        ) : (
          <p
            className={classNames(
              "flex justify-center items-center m-0 text-white rounded-full bg-primaryblue",
              size,
              textSize,
              fontWeight
            )}
          >
            {name
              .split(" ")
              .map((n) => n[0])
              .slice(0, 2)
              .join("")
              .toUpperCase()}
          </p>
        )}
      </>
    );
  };

  const formatPhoneNumber = (input) => {
    const cleaned = ("" + input).replace(/\D/g, "");

    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    return match
      ? [match[1], match[2], match[3]].filter(Boolean).join("-")
      : "";
  };

  const clearInput = () => {
    setStaffFirstName("");
    setStaffLastName("");
    setStaffEmail("");
    setStaffPhone("");
    setStaffRole(0);
    setError("");
  };

  const tabName = {
    staff: "Staff",
    providers: "Providers",
    patients: "Patients",
    zones: "Zones",
  };

  const tabCount = {
    staff: staff.length,
    providers: providers.length,
    patients: patients.length,
    zones: zones.length,
  };

  return (
    <div className="flex flex-row items-start">
      {isLoading && <Loading loading={isLoading} />}
      <div className="py-4 pr-8 pl-16 w-4/12">
        <div className="w-full overflow-x-auto overflow-y-auto h-[calc(100vh-128px)] bg-white rounded-lg shadow border-1 border-primarygrayborder">
          <button className="p-3 text-primaryblue" onClick={handleBack}>
            Back
          </button>
          <div className="flex flex-col gap-2 justify-end items-center p-4 border-b border-b-primarygrayborder">
            <div className="relative">
              <Avatar
                name={community?.name ?? ""}
                image={null}
                size="w-20 h-20"
                textSize="text-3xl"
                fontWeight="font-semibold"
              />
              <div className="absolute right-0 bottom-14 w-4 h-4 rounded-full bg-primarygreen" />
            </div>
            {community && (
              <p className="m-0 text-lg font-bold">{community?.name}</p>
            )}
           {community?.type != null && community?.type !== "" && (
            <p className="m-0 text-sm text-primarytextgrey">
              {community?.type}
            </p>
           )}
           {community?.address != null && community?.address !== "" && (
            <p className="m-0 text-sm text-primarytextgrey">
              {community?.address}
            </p>
           )}
            {community && community.email != null && community.email !== "" && (
              <p className="m-0 text-sm text-primarytextgrey">
                {community.email}
              </p>
            )}
            {community && community.phone != null && community.phone !== "" && (
              <p className="m-0 text-sm text-primarytextgrey">
                {community.phone} - {community.room_extension}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-4 p-4 h-auto">
            <p className="m-0 text-lg font-bold">Service Information</p>
            <button
              onClick={() => handleTabChange("patients")}
              className="flex gap-4 items-center cursor-pointer"
            >
              <div
                className={classNames(
                  "flex items-center justify-center w-10 h-10 rounded-md",
                  tab === "patients" && "bg-subtleblue"
                )}
              >
                <PeopleSharp
                  style={{
                    color: tab === "patients" ? "#3062D4" : "#000",
                    fontSize: "20px",
                  }}
                />
              </div>
              <p
                className={classNames(
                  "m-0 font-semibold",
                  tab === "patients" && "text-primaryblue"
                )}
              >
                Patients
              </p>
            </button>
            <button
              onClick={() => handleTabChange("staff")}
              className="flex gap-4 items-center cursor-pointer"
            >
              <div
                className={classNames(
                  "flex items-center justify-center w-10 h-10 rounded-md",
                  tab === "staff" && "bg-subtleblue"
                )}
              >
                <PeopleSharp
                  style={{
                    color: tab === "staff" ? "#3062D4" : "#000",
                    fontSize: "20px",
                  }}
                />
              </div>
              <p
                className={classNames(
                  "m-0 font-semibold",
                  tab === "staff" && "text-primaryblue"
                )}
              >
                Staff
              </p>
            </button>
            <button
              onClick={() => handleTabChange("providers")}
              className="flex gap-4 items-center cursor-pointer"
            >
              <div
                className={classNames(
                  "flex items-center justify-center w-10 h-10 rounded-md",
                  tab === "providers" && "bg-subtleblue"
                )}
              >
                <PeopleSharp
                  style={{
                    color: tab === "providers" ? "#3062D4" : "#000",
                    fontSize: "20px",
                  }}
                />
              </div>
              <p
                className={classNames(
                  "m-0 font-semibold",
                  tab === "providers" && "text-primaryblue"
                )}
              >
                Providers
              </p>
            </button>
            {/* <button
              onClick={() => handleTabChange("facilities")}
              className="flex gap-4 items-center cursor-pointer"
            >
              <div
                className={classNames(
                  "flex items-center justify-center w-10 h-10 rounded-md",
                  tab === "facilities" && "bg-subtleblue"
                )}
              >
                <HouseSharp
                  style={{
                    color: tab === "facilities" ? "#3062D4" : "#000",
                    fontSize: "20px",
                  }}
                />
              </div>
              <p
                className={classNames(
                  "m-0 font-semibold",
                  tab === "facilities" && "text-primaryblue"
                )}
              >
                Facilities
              </p>
            </button> */}
          </div>
          {/* {process.env.REACT_APP_ENV === "dev" && (
            <div className="flex flex-col gap-4 p-4 h-auto border-t border-t-primarygrayborder">
              <p className="m-0 text-lg font-bold">Additional Information</p>
              <div className="flex gap-4 items-center">
                {community && (
                  <Tooltip title="Zones">
                    <div className="flex justify-center items-center w-10 h-10 bg-blue-50 rounded-md">
                      <MyLocation
                        style={{
                          color: "#828282",
                          fontSize: "20px",
                        }}
                      />
                    </div>
                  </Tooltip>
                )}
                <div className="flex flex-row flex-wrap items-center">
                  <p className="m-0">
                    {zones.length > 0
                      ? zones.map((zone) => zone.name).join(", ")
                      : "NA"}
                  </p>
                  <button
                    className="px-3 text-[#828282]"
                    onClick={() => setZoneEditModalOpen(true)}
                  >
                    <Edit />
                  </button>
                </div>
              </div>
            </div>
          )} */}
        </div>
      </div>
      <div className="flex flex-col items-start py-4 w-8/12">
        <div className="flex flex-row justify-between items-center pr-16 pl-8 w-full">
          <div className="flex flex-row gap-12 items-center w-full">
            <h1 className="m-0 text-2xl font-bold whitespace-nowrap w-fit">
              {tabName[tab]}{" "}
              <span className="ml-2 text-base font-bold text-primaryblue">
                {tabCount[tab]}
              </span>
            </h1>
            <div className="flex flex-row gap-8 items-center w-full max-w-7xl">
              <Search value={searchFilter} setValue={setSearchFilter} />
              {tab === "zones" && (
                <button
                  onClick={() => {
                    setZoneEditModalOpen(true);
                  }}
                  className="px-4 py-2 shadow-sm rounded-md border-[2px] border-primarygrayborder whitespace-nowrap text-primaryblue"
                >
                  Edit Zones
                </button>
              )}
            </div>
          </div>
          {tab === "staff" && user?.permissions?.includes(`communities_dm.edit_staff`) && (
            <button
              onClick={() => {
                setAddStaffOpen(true);
              }}
              className="px-4 py-2 shadow-sm rounded-md border-[2px] border-primarygrayborder whitespace-nowrap text-primaryblue"
            >
              Add New
            </button>
          )}
        </div>
        <div className="pr-16 pl-8 w-full">
          <div className="relative w-full mt-8 mb-2 overflow-x-auto overflow-y-auto bg-white rounded-lg shadow max-h-[calc(100vh-200px)]">
            {tab === "patients" ? (
              <table className="relative w-full bg-white border-collapse table-auto whitespace-no-wrap table-striped">
                <thead>
                  <tr className="h-16 text-base font-bold text-left text-white whitespace-nowrap bg-primaryblue">
                    <th className="px-4 py-3">
                      <p className="m-0">Name</p>
                    </th>
                    <th className="px-4 py-3">Provider</th>
                    <th className="px-4 py-3">Last Visit Date</th>
                    <th className="px-4 py-3">DOB</th>
                    <th className="px-4 py-3">Gender</th>
                    <th className="px-4 py-3">EHR ID</th>
                    <th className="px-4 py-3">
                      <p className="m-0">Status</p>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {patients
                    .filter((patient) =>
                      patient.name
                        .toLowerCase()
                        .includes(searchFilter.toLowerCase())
                    )
                    .map((patient, index) => {
                      return (
                        <PatientsTable
                          patient={patient}
                          index={index}
                          handlePatientClick={handlePatientClick}
                          patients={patients}
                        />
                      );
                    })}
                </tbody>
              </table>
            ) : tab === "providers" ? (
              <table className="relative w-full bg-white border-collapse table-auto whitespace-no-wrap table-striped">
                <thead>
                  <tr className="h-16 text-base font-bold text-left text-white whitespace-nowrap bg-primaryblue">
                    <th className="px-4 py-3">
                      <div className="flex flex-row gap-2 items-center">
                        <p className="m-0">Name</p>
                      </div>
                    </th>
                    <th className="px-4 py-3">
                      <div className="flex flex-row gap-2 items-center">
                        <p className="m-0">Speciality</p>
                      </div>
                    </th>
                    <th className="px-4 py-3">NPI ID</th>
                    <th className="px-4 py-3">Phone No.</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Address</th>
                    <th className="px-4 py-3">Zone</th>
                    <th className="px-4 py-3">
                      <div className="flex flex-row gap-2 items-center">
                        <p className="m-0">Status</p>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {providers
                    .filter((provider) =>
                      provider.name
                        .toLowerCase()
                        .includes(searchFilter.toLowerCase())
                    )
                    .map((provider, index) => (
                      <tr
                        className={classNames(
                          "text-left text-gray-900 h-24 whitespace-nowrap",
                          index !== providers.length - 1
                            ? "border-b"
                            : "border-none"
                        )}
                        key={index}
                      >
                        <td className="px-4 py-3 text-left">
                          <div className="flex flex-row gap-4 items-center">
                            <button
                              onClick={() => handleProviderClick(provider)}
                              className="flex flex-row gap-4 items-center"
                            >
                              <Avatar
                                name={provider.name}
                                image={provider.profileImage}
                              />
                              <p className="m-0 font-semibold underline">
                                {provider.name}
                              </p>
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-left text-gray-700">
                          {provider.speciality}
                        </td>
                        <td className="px-4 py-3 text-left text-gray-700">
                          {provider.npi_id}
                        </td>
                        <td className="px-4 py-3 text-left text-gray-700">
                          {provider.phone}
                        </td>
                        <td className="px-4 py-3 text-left text-gray-700">
                          {provider.email}
                        </td>
                        <td className="px-4 py-3 text-left text-gray-700">
                          {provider.address.join(", ")}
                        </td>
                        <td className="px-4 py-3 text-left text-gray-700">
                          {provider.zones.length > 0
                            ? provider.zones.map((zone) => zone.name).join(", ")
                            : "NA"}
                        </td>
                        <td className="px-4 py-3 text-left text-gray-700">
                          <div
                            className={classNames(
                              provider.status.toUpperCase() === "ACTIVE"
                                ? "bg-subtlegreen border-[1px] border-primarygreen text-primarygreen"
                                : "bg-primarylightgrey border-[1px] border-primarygrayborder text-primarytextgrey",
                              "rounded-md text-center flex flex-row gap-1 items-center px-2 w-fit"
                            )}
                          >
                            <div
                              className={classNames(
                                "w-2 h-2 rounded-full",
                                provider.status.toUpperCase() === "ACTIVE"
                                  ? "bg-primarygreen"
                                  : "bg-primarytextgrey"
                              )}
                            />
                            {provider.status}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            ) : tab === "staff" ? (
              <table className="relative w-full bg-white border-collapse table-auto whitespace-no-wrap table-striped">
                <thead>
                  <tr className="h-16 text-base font-bold text-left text-white whitespace-nowrap bg-primaryblue">
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Phone</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {staff
                    .filter((staff) =>
                      staff.name
                        .toLowerCase()
                        .includes(searchFilter.toLowerCase())
                    )
                    .map((staff, index) => (
                      <tr
                        className={classNames(
                          "text-left text-gray-900 h-24 whitespace-nowrap",
                          index !== staff.length - 1
                            ? "border-b"
                            : "border-none"
                        )}
                        key={index}
                      >
                        <td className="px-4 py-3 text-left">
                          <button className="flex flex-row gap-4 items-center">
                            <Avatar name={staff.name} image={null} />
                            <p className="m-0 font-semibold underline">
                              {staff.name}
                            </p>
                          </button>
                        </td>
                        <td className="px-4 py-3 text-left text-gray-700">
                          {staff.email}
                        </td>
                        <td className="px-4 py-3 text-left text-gray-700">
                          {staff.phone}
                        </td>
                        <td className="px-4 py-3 text-left text-gray-700">
                          {staff.role === 1
                            ? "Nurse"
                            : staff.role === 2
                              ? "Admin"
                              : "Supervisor"}
                        </td>
                        <td className="px-4 py-3 text-left text-gray-700">
                          <div
                            className={classNames(
                              staff.status.toUpperCase() === "ACTIVE"
                                ? "bg-subtlegreen border-[1px] border-primarygreen text-primarygreen"
                                : "bg-primarylightgrey border-[1px] border-primarygrayborder text-primarytextgrey",
                              "rounded-md text-center flex flex-row gap-1 items-center px-2 w-fit"
                            )}
                          >
                            <div
                              className={classNames(
                                "w-2 h-2 rounded-full",
                                staff.status.toUpperCase() === "ACTIVE"
                                  ? "bg-primarygreen"
                                  : "bg-primarytextgrey"
                              )}
                            />
                            {staff.status}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-left text-gray-700">
                          <button
                            onClick={() => removeCommunityUser(staff.id)}
                            className="flex justify-center items-center w-8 h-8 rounded-md aspect-square bg-primarylightgrey"
                          >
                            <DeleteOutline
                              style={{
                                fontSize: "20px",
                                color: "#EB5757",
                              }}
                            />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            ) : tab === "zones" ? (
              <table className="relative w-full bg-white border-collapse table-auto whitespace-no-wrap table-striped">
                <thead>
                  <tr className="h-16 text-base font-bold text-left text-white whitespace-nowrap bg-primaryblue">
                    <th className="px-4 py-3">Name</th>
                  </tr>
                </thead>
                <tbody>
                  {zones.map((zone, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 text-left text-gray-700">
                        {zone.name}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : tab === "facilities" ? (
              <table className="relative w-full bg-white border-collapse table-auto whitespace-no-wrap table-striped">
                <thead>
                  <tr className="h-16 text-base font-bold text-left text-white whitespace-nowrap bg-primaryblue">
                    <th className="px-4 py-3">Facility Code</th>
                    <th className="px-4 py-3">Facility Name</th>
                    <th className="px-4 py-3">Facility Type</th>
                    <th className="px-4 py-3">Facility Type ID</th>
                  </tr>
                </thead>
                <tbody>
                  {community?.facilities?.map((facility, index) => {
                    return (
                      <tr key={index}>
                        <td className="px-4 py-3 text-left text-gray-700">
                          {facility.id}
                        </td>
                        <td className="px-4 py-3 text-left text-gray-700">
                          {facility.name}
                        </td>
                        <td className="px-4 py-3 text-left text-gray-700">
                          {facility.type}
                        </td>
                        <td className="px-4 py-3 text-left text-gray-700">
                          {facility.typeId}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : null}
          </div>
        </div>
      </div>
      <ReusableModal open={addStaffOpen} hideHeader>
        <div className="flex flex-col items-center min-w-[480px]">
          {isCreatingStaffUser && <Loading loading={isCreatingStaffUser} />}
          <div className="flex flex-row justify-between items-center p-6 w-full">
            <p className="m-0 text-2xl font-bold text-primaryblack">
              Send Invite
            </p>
            <button
              onClick={() => {
                setAddStaffOpen(false);
                clearInput();
              }}
              className="flex border-[2px] border-primarygrayborder items-center justify-center w-8 rounded-full aspect-square bg-primarylightgrey"
            >
              <CloseSvg color={"#828282"} />
            </button>
          </div>
          <div className="h-[1px] w-full bg-primarygrayborder" />
          <div className="flex flex-col gap-4 p-6">
            <div className="flex flex-row gap-4 w-full">
              <div className="flex flex-col gap-2 w-1/2">
                <label htmlFor="email" className="m-0 text-base font-semibold">
                  First Name
                </label>
                <input
                  type="text"
                  id="firstname"
                  name="firstname"
                  placeholder="John"
                  className="w-full p-2 border-[1px] border-primarygray rounded-md outline-none"
                  value={staffFirstName}
                  onChange={(e) => setStaffFirstName(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-2 w-1/2">
                <label htmlFor="phone" className="m-0 text-base font-semibold">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastname"
                  name="lastname"
                  placeholder="Doe"
                  className="w-full p-2 border-[1px] border-primarygray rounded-md outline-none"
                  value={staffLastName}
                  onChange={(e) => setStaffLastName(e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-col gap-2 w-full">
              <label htmlFor="email" className="m-0 text-base font-semibold">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Enter Email"
                className="w-full p-2 border-[1px] border-primarygray rounded-md outline-none"
                value={staffEmail}
                onChange={(e) => setStaffEmail(e.target.value)}
              />
            </div>
            <div className="flex flex-row gap-4 w-full">
              <div className="flex flex-col gap-2 w-1/2">
                <label htmlFor="phone" className="m-0 text-base font-semibold">
                  Phone (optional)
                </label>
                <input
                  type="tel"
                  id="tel"
                  name="tel"
                  className="w-full p-2 border-[1px] border-primarygray rounded-md outline-none"
                  placeholder="xxx-xxx-xxxx"
                  value={staffPhone}
                  onChange={(e) => {
                    if (e.target.value.length >= 13) {
                      return;
                    }
                    setStaffPhone(formatPhoneNumber(e.target.value));
                  }}
                />
              </div>
              <div className="flex flex-col gap-2 w-1/2">
                <label htmlFor="role" className="m-0 text-base font-semibold">
                  Role
                </label>
                <select
                  id="role"
                  name="role"
                  value={staffRole}
                  onChange={(e) => setStaffRole(e.target.value)}
                  className="w-full p-2 border-[1px] border-primarygray rounded-md outline-none"
                >
                  <option value={0}>Select Role</option>
                  <option value={1}>Nurse</option>
                  <option value={2}>Admin</option>
                  <option value={3}>Supervisor</option>
                </select>
              </div>
            </div>
            {error && <p className="text-red-500">{error}</p>}
            <button
              disabled={
                staffFirstName === "" ||
                staffLastName === "" ||
                staffEmail === "" ||
                // staffPhone === "" ||
                staffRole === 0
              }
              className="w-full disabled:opacity-40 disabled:cursor-not-allowed font-semibold p-2 border-[2px] rounded-md bg-subtleblue border-primaryblue text-primaryblue whitespace-nowrap"
              onClick={saveCommunityUser}
            >
              Invite
            </button>
          </div>
        </div>
      </ReusableModal>
      {community && (
        <CommunityZoneEditModal
          open={zoneEditModalOpen}
          setOpen={setZoneEditModalOpen}
          community={community}
          updateCommunityZones={updateCommunityZones}
          communityZones={zones}
        />
      )}
    </div>
  );
};

export default CommunityInfo;
